import React from "react";
import { useTranslation } from "react-i18next";

import { PriceTag } from "@airhelp/icons";
import { Flex, VStack, Image, Text, useBreakpointValue, HStack } from "@chakra-ui/react";

type IComponent = {
  imageSrc: string;
  imageText: string;
  description: string;
  logoSrc: string;
  onClick: () => void;
  discount: number | null;
  dataTestId: string;
};

const PerkBanner: React.FC<IComponent> = ({
  imageSrc,
  imageText,
  description,
  logoSrc,
  onClick,
  discount,
  dataTestId,
}) => {
  const { t } = useTranslation();
  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <Flex
      flex="1"
      bgColor="greyscale.100"
      p={{ base: 0, md: 4 }}
      position="relative"
      justifyContent="flex-start"
      gap={8}
      borderRadius="xl"
      onClick={onClick}
      minWidth={{ base: "148px", md: "350px" }}
      data-testid={`${dataTestId}-banner`}
      cursor="pointer"
    >
      <Image
        src={imageSrc}
        height="162px"
        objectFit="cover"
        objectPosition="right"
        overflow="hidden"
        minWidth={{ base: "100%", md: "162px" }}
        borderRadius="md"
      />
      {discount && (
        <HStack
          height="26px"
          bgColor="success.500"
          position="absolute"
          bottom={{ base: "unset", md: 6 }}
          top={{ base: 4, md: "unset" }}
          left={{ base: 4, md: 6 }}
          p={1}
          borderRadius="sm"
          spacing="6px"
          data-testid={`${dataTestId}-banner-discount-tag`}
        >
          <PriceTag color="white" boxSize={3} />
          <Text color="greyscale.100" fontSize="xs" fontWeight="bold">
            {t("dashboard.perks.percentage_off", { percentage: discount })}
          </Text>
        </HStack>
      )}
      {isMobile ? (
        <Text color="greyscale.100" fontSize="sm" fontWeight="medium" position="absolute" bottom={4} left={4}>
          {imageText}
        </Text>
      ) : (
        <VStack alignItems="flex-start" justifyContent="space-between" height="100%">
          <Image src={logoSrc} h={4} />
          <VStack alignItems="flex-start" spacing={0}>
            <Text fontWeight="medium" fontSize="md">
              {imageText}
            </Text>
            <Text fontSize="sm" color="greyscale.600">
              {description}
            </Text>
          </VStack>
        </VStack>
      )}
    </Flex>
  );
};

export default PerkBanner;
